/**
 * @file shipping-list.ts
 *
 * Pure row builders for the Shipping overview's two lists. They turn the store's
 * zones (+ their methods and locations) and the shop pickup-locations array
 * into flat, presentational rows — the method-type and "ships to" summaries and
 * the pickup address line — so the section components stay declarative and the
 * mapping stays testable without React.
 */

import type {
  ShippingMethod,
  ShippingZone,
  ShippingZoneLocation,
  ShopContinent,
  ShopPickupLocation,
} from '../api/shop-types';
import { methodTypeIds } from './shipping-method-utils';
import { FLAT_RATE } from './shipping-constants';
import { formatPickupAddress, formatShippingLocations, isAllRegions } from './shipping-region-utils';

/** A custom-shipping zone as shown in the overview list. */
export interface ZoneRow {
  id: string;
  zoneId: string;
  name: string;
  /** Comma-joined method-type labels, or the "no methods" fallback. */
  methodTypeLabel: string;
  /** Region summary, or the "all regions" label. */
  shipsToLabel: string;
}

/** A local-pickup location as shown in the overview list. */
export interface PickupRow {
  id: string;
  name: string;
  address: string;
}

/** Localized labels needed to build the zone rows. */
export interface ZoneRowLabels {
  flatRate: string;
  freeShipping: string;
  allRegions: string;
  noMethods: string;
}

/**
 * Builds the custom-shipping zone rows, preserving the input zone order.
 *
 * @param zones - The zones in display order.
 * @param zoneMethods - Methods keyed by zone id.
 * @param zoneLocations - Locations keyed by zone id.
 * @param continents - The region tree (for region names).
 * @param labels - Localized method-type / all-regions / no-methods labels.
 *
 * @returns The zone rows.
 */
export function buildZoneRows(
  zones: ShippingZone[],
  zoneMethods: Record<string, ShippingMethod[]>,
  zoneLocations: Record<string, ShippingZoneLocation[]>,
  continents: ShopContinent[],
  labels: ZoneRowLabels,
): ZoneRow[] {
  return zones.map((zone) => {
    const methods = zoneMethods[zone.id] ?? [];
    const locations = zoneLocations[zone.id] ?? [];
    const typeIds = methodTypeIds(methods);
    const methodTypeLabel = typeIds.length
      ? typeIds
          .map((id) => (id === FLAT_RATE ? labels.flatRate : labels.freeShipping))
          .join(', ')
      : labels.noMethods;
    const shipsToLabel = isAllRegions(locations)
      ? labels.allRegions
      : formatShippingLocations(locations, continents);
    return {
      id: String(zone.id),
      zoneId: zone.id,
      name: zone.name,
      methodTypeLabel,
      shipsToLabel,
    };
  });
}

/**
 * Builds the local-pickup rows from the `/v1` pickup locations (the location `id` is the
 * row's stable key, the drag-reorder key, and the edit-route param).
 *
 * @param locations - The pickup locations.
 * @param unnamedLabel - Fallback name for a location with a blank name.
 *
 * @returns The pickup rows.
 */
export function buildPickupRows(
  locations: ShopPickupLocation[],
  unnamedLabel: string,
): PickupRow[] {
  return locations.map((location) => ({
    id: location.id,
    name: location.name || unnamedLabel,
    address: formatPickupAddress(location),
  }));
}
